import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseActionableDialog } from "@/shared/ui/base-actionable-dialog";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { Box, Container, Stack, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { type DeepPartial, useForm } from "react-hook-form";
import type { AdminCargoDetailPageProps } from "../../model";
import {
  type CreateOfferCargoFormSchemaType,
  createOfferCargoFormSchema,
} from "../../model/validation";
import { CompanyInfo } from "../company-info";
import { DeliveryProposalsInfo } from "../delivery-proposals-info";
import { CargoForm } from "./forms/cargo-form";
import { DeliveryProposalForm } from "./forms/delivery-proposal-form";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { ScheduleForm } from "./forms/schedule-form";
import { VehicleForm } from "./forms/vehicle-form";

export const CargoCreateInProgress: React.FC<AdminCargoDetailPageProps> = ({
  defaultInputValues,
  offerCargoProcess,
  onConfirmClick,
  onInputValuesUpdate,
  onProcessSuspend,
  onCopyCargoClick,
}) => {
  // 初期化フラグ
  const [
    hasDefaultInputValuesInitialized,
    setDefaultInputValuesHasInitialized,
  ] = useState(false);
  const [hasOfferCargoInitialized, setHasOfferCargoInitialized] =
    useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const links = [
    { href: "/admin/cargos", label: "荷物一覧" },
    { label: "荷物詳細" },
  ];

  // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
  const [destinationFiles, setDestinationFiles] = useState<File[]>();

  const { handleSubmit, control, watch, getValues, formState, reset, trigger } =
    useForm<CreateOfferCargoFormSchemaType>({
      resolver: customZodResolver(createOfferCargoFormSchema),
      mode: "onChange",
      defaultValues: {
        highwayFee: 1,
        destinations: [
          {
            hasLoading: true,
            hasUnloading: false,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
          {
            hasLoading: false,
            hasUnloading: true,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
        ],
      },
    });

  // offerCargoProcessのロードを待って、デフォルト値としてセットする
  useLoggingEffect(
    () => {
      // localCacheがある場合の初期化（一度だけ実行）
      if (defaultInputValues && !hasDefaultInputValuesInitialized) {
        reset(defaultInputValues);
        setDefaultInputValuesHasInitialized(true);
        return;
      }

      // 荷物データが取得できた場合の初期化（一度だけ実行）
      if (
        offerCargoProcess.status === "success" &&
        offerCargoProcess.data.draftOfferCargo &&
        !hasOfferCargoInitialized &&
        !defaultInputValues
      ) {
        const cargo = offerCargoProcess.data.draftOfferCargo;
        const initialValues = replaceNullWithUndefined({
          ...cargo,
          companyId: cargo.company?.id,
          destinations: cargo.destinations.map((dest) => ({
            ...dest,
            city: dest.address?.split(" ")[0],
            addressLine:
              dest.address?.split(" ").slice(1).join(" ") || undefined,
          })),
        });

        reset(initialValues);
        trigger();
        setHasOfferCargoInitialized(true);
      }
    },
    [
      offerCargoProcess,
      reset,
      defaultInputValues,
      hasDefaultInputValuesInitialized,
      hasOfferCargoInitialized,
    ],
    "AdminCargoDetailInProgress",
  );

  const handleInputValuesUpdate = (
    values: DeepPartial<CreateOfferCargoFormSchemaType>,
  ) => {
    onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  };
  useSubscribeFromValues(handleInputValuesUpdate, watch, true);

  const hasErrors = Object.keys(formState.errors).length > 0;

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          荷物詳細
        </Typography>
        <BaseButton
          startIcon={<BaseIcon name="plus" htmlColor="#52A48B" />}
          variant="outlined"
          onClick={onCopyCargoClick}
        >
          コピーして荷物登録する
        </BaseButton>
      </Box>

      <Box
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <form
          onSubmit={handleSubmit((data) => {
            onConfirmClick({
              ...data,
              stage: "draft",
              destinationFiles,
            });
          })}
        >
          <Box
            display="flex"
            bgcolor="grey.50"
            minHeight="600px"
            flexDirection={"column"}
            flexGrow={1}
          >
            <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
              <Stack spacing={3} sx={{ mt: 4, mb: { xs: 4, sm: 10 }, py: 2 }}>
                <CompanyInfo offerCargoProcess={offerCargoProcess} />
                <ScheduleForm control={control} />
                <DestinationsForm
                  isEasyForm={false}
                  control={control}
                  formState={formState}
                  watch={watch}
                  onDestinationFilesChange={setDestinationFiles} // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
                  onDestinationInsertOrRemove={() => {
                    // useFieldArrayのinsert/removeがwatchを発火させないため、手動で更新
                    handleInputValuesUpdate(getValues());
                  }}
                />
                <PaymentForm isEasyForm={false} control={control} />
                <CargoForm isEasyForm={false} control={control} />
                <VehicleForm isEasyForm={false} control={control} />
                <MemoForm control={control} />
                <DeliveryProposalForm control={control} />
              </Stack>
            </Container>
          </Box>
          <Stack spacing={2} p={2} alignItems="center" justifyContent="center">
            {hasErrors && (
              <BaseMessageCard
                status="error"
                message={
                  "正しく入力されていない項目があります。再度ご確認ください。"
                }
              />
            )}
            <Stack direction="row" spacing={2} alignItems="center">
              <BaseButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!defaultInputValues || hasErrors}
                sx={{ width: "240px" }}
              >
                確認する
              </BaseButton>
              <BaseButton
                variant="outlined"
                color="warning"
                sx={{ fontSize: "14px", width: "240px", fontWeight: 700 }}
                startIcon={<BaseIcon name="minusCircle" htmlColor="#EA583B" />}
                onClick={() => setIsCancelModalOpen(true)}
              >
                この荷物の配送提案を受付停止する
              </BaseButton>
            </Stack>
          </Stack>
        </form>

        {/* 配送提案一覧 */}
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 0 }, mt: 4 }}>
          <DeliveryProposalsInfo
            offerCargoProcess={offerCargoProcess}
            draftOfferCargoId={
              offerCargoProcess.status === "success"
                ? offerCargoProcess.data.draftOfferCargo?.id
                : undefined
            }
          />
        </Container>
      </Box>

      {/* 受付停止ダイアログ */}
      <BaseActionableDialog
        title="配送提案の受付を停止します"
        open={isCancelModalOpen}
        actionButtonLabel="停止する"
        cancelButtonLabel="キャンセル"
        onClose={() => setIsCancelModalOpen(false)}
        onSubmit={() => {
          onProcessSuspend();
          setIsCancelModalOpen(false);
        }}
      >
        <Stack spacing={2} mb={4}>
          <Box>
            <Typography variant="body2" fontWeight={400}>
              停止すると、配送業者から新規の提案が届かなくなり、契約の作成もできません。
            </Typography>
            <Typography variant="body2" fontWeight={400} color="#EA583B">
              また、停止した場合、再開はできません。
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={400}>
              ※ 受付停止は下記を満たした場合のみ可能です。
            </Typography>
            <Typography variant="body2" fontWeight={400}>
              <b>・</b>
              配送業者からの提案が0件、あるいは全ての提案が「見送り」されている
            </Typography>
            <Typography variant="body2" fontWeight={400}>
              <b>・</b>契約が作成されていない
            </Typography>
          </Box>
        </Stack>
      </BaseActionableDialog>
    </Box>
  );
};
